import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  House, Plus, UserCircle, BookOpen, FilmStrip, Microphone, HandsPraying,
  Sparkle, Hash, NotePencil, X, Headphones, Sun, ForkKnife, UploadSimple,
  SignIn, SignOut,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useMiniPlayer } from "../context/MiniPlayerContext";
import UserAvatar from "./UserAvatar";

/**
 * BottomNav — Holy Words mobile bottom toolbar.
 *
 * Inspired by YouTube's 5-slot bar (the user's reference) but the
 * slots are picked to match what makes THIS app special:
 *
 *   1. 🏠 Home          → /
 *   2. 🎧 Listen        → resumes the last sermon/song/Bible track the
 *                         user played. Pulses a terracotta dot when
 *                         something is paused mid-track, so it acts as
 *                         a "pick back up" magnet.
 *   3. ➕ Center "+"    → single-tap opens a quick-create sheet
 *                         (Picture Bible / Start a Room / Post a Prayer
 *                         / Write a Note / etc). LONG-PRESS jumps
 *                         straight to "Post a Prayer" — the most-used
 *                         create action in a faith app.
 *   4. ✨ Today          → /devotional. Today's devotional + a date
 *                         pill so it feels alive ("MAY 17").
 *   5. 👤 You            → avatar (if signed in) → /me.
 *
 * iOS safe-area aware. Hidden on tablets/desktop where the chip rail
 * + side QuickShortcutsRail cover navigation.
 */

const ACTIONS = [
  { to: "/community-shorts/upload", label: "Upload a Short", icon: UploadSimple },
  { to: "/recipes", label: "Sunday Recipes", icon: ForkKnife },
  { to: "/shorts", label: "Discover Shorts", icon: Sparkle },
  { to: "/storybook", label: "Picture Bible", icon: Sparkle },
  { to: "/rooms", label: "Start a Room", icon: Hash },
  { to: "/prayer-wall", label: "Post a Prayer", icon: HandsPraying },
  { to: "/my-notes", label: "Write a Note", icon: NotePencil },
  { to: "/sermons", label: "Browse Sermons", icon: Microphone },
  { to: "/movies", label: "Watch a Film", icon: FilmStrip },
  { to: "/bible", label: "Open the Bible", icon: BookOpen },
];

// Long-press detection threshold. Matches Android's default 500 ms so
// muscle memory carries over from the user's phone.
const LONG_PRESS_MS = 500;

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth?.() || { user: null };
  const { open: playerOpen, resume: resumeMiniPlayer } = useMiniPlayer();
  const [showCreate, setShowCreate] = useState(false);
  const [lastPlayed, setLastPlayed] = useState(() => readLastPlayed());
  const pressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);

  // Keep the Listen slot in sync. We listen for the custom event the
  // MiniPlayerContext now emits whenever a new track starts, so the
  // bar updates without a route change. We also listen for `storage`
  // so the bar reflects changes from another tab.
  useEffect(() => {
    const refresh = () => setLastPlayed(readLastPlayed());
    window.addEventListener("hw:last-played:changed", refresh);
    window.addEventListener("storage", (e) => {
      if (e.key === "hw:last-played") refresh();
    });
    return () => {
      window.removeEventListener("hw:last-played:changed", refresh);
    };
  }, []);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  // Center "+" gesture handlers. We use pointer events so it works
  // for mouse, touch, and stylus uniformly. The long-press timer is
  // reset on pointer-up; if it actually fires, we set a ref that
  // suppresses the subsequent click so we don't ALSO open the sheet.
  const startPress = (e) => {
    longPressFiredRef.current = false;
    pressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      // Haptic where supported — feels premium on Android Chrome.
      try { navigator.vibrate?.(35); } catch (_) {}
      navigate("/prayer-wall");
    }, LONG_PRESS_MS);
  };
  const cancelPress = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };
  const handleCreateClick = () => {
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return; // long-press already handled navigation
    }
    setShowCreate(true);
  };

  // Auto-hide on scroll. When the user scrolls DOWN we slide the
  // bottom nav off-screen so the page (and the MiniPlayer that sits
  // above it) regain a full viewport. On scroll UP we slide it back
  // in. User report (Feb 19 2026): the MiniPlayer kept ending up
  // tucked behind the nav even with bottomReserve in place — autohide
  // gives users a guaranteed escape hatch (just flick down to reveal
  // whatever's hiding underneath).
  //
  // Feb 26 follow-up: user reported "the black tool bar does not
  // disappear" on the Bible page even while actively scrolling. The
  // original delta threshold (>6 px) was too aggressive for slow
  // reader-style scrolls. We now:
  //   • hide on ANY downward scroll once past 60 px,
  //   • re-show on upward scroll OR on a tap on the bottom edge,
  //   • idle-hide after 1.6 s of no scroll movement (so when the user
  //     stops to read a verse, the bar gets out of their face).
  const [navHidden, setNavHidden] = useState(false);
  const lastScrollYRef = useRef(typeof window !== "undefined" ? window.scrollY : 0);
  const idleTimerRef = useRef(null);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const clearIdle = () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
    const armIdleHide = () => {
      clearIdle();
      idleTimerRef.current = setTimeout(() => {
        if (window.scrollY > 60) setNavHidden(true);
      }, 1600);
    };
    const onScroll = () => {
      const y = window.scrollY;
      const last = lastScrollYRef.current;
      const delta = y - last;
      if (y < 60) {
        setNavHidden(false);
        clearIdle();
      } else if (delta > 2) {
        setNavHidden(true);
        clearIdle();
      } else if (delta < -2) {
        setNavHidden(false);
        armIdleHide();
      } else {
        armIdleHide();
      }
      lastScrollYRef.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // First-load idle hide so users immediately see the bar can move.
    armIdleHide();
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearIdle();
    };
  }, []);

  // Reveal on tap of the bottom edge of the screen — gives users a
  // reliable way to bring the bar back without having to scroll up.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onTouch = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      if (t.clientY > window.innerHeight - 24) setNavHidden(false);
    };
    window.addEventListener("touchstart", onTouch, { passive: true });
    return () => window.removeEventListener("touchstart", onTouch);
  }, []);

  return (
    <>
      <nav
        data-testid="bottom-nav"
        aria-label="Primary"
        className={`md:hidden fixed bottom-0 left-0 right-0 z-[60] bg-ink text-cream border-t border-cream/10 transition-transform duration-300 ease-out ${
          navHidden ? "translate-y-full" : "translate-y-0"
        }`}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <ul className="grid grid-cols-7 items-end h-16">
          <NavSlot
            to="/"
            label="Home"
            Icon={House}
            active={isActive("/")}
            testid="bn-home"
          />

          {/* Listen — resumes last played media when the user has any,
              otherwise routes to the sermons library so they can pick
              their first one. Pulsing dot when a track is in progress. */}
          <ListenSlot
            lastPlayed={lastPlayed}
            playerOpen={playerOpen}
            onResume={resumeMiniPlayer}
            active={isActive("/sermons") || isActive("/gospel-music") || isActive("/music-videos") || isActive("/christian-rap")}
          />

          {/* AI wizard — on every page (owner request: mobile top bar
              was too crowded, so it lives down here now). */}
          <li className="h-full">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("holywords:open-ai"))}
              data-testid="bn-ai"
              aria-label="Ask Holy Words AI"
              className="w-full h-full flex flex-col items-center justify-center gap-0.5 pt-1 text-cream/70 active:scale-95 transition-transform"
            >
              <Sparkle size={22} weight="fill" className="text-[#E7C47C]" />
              <span className="text-[9px] font-semibold leading-none pb-1.5">AI</span>
            </button>
          </li>

          {/* Center "+" — single tap opens the action sheet, long-press
              jumps straight to the Prayer Wall composer. */}
          <li className="flex items-center justify-center -mt-6">
            <button
              type="button"
              onClick={handleCreateClick}
              onPointerDown={startPress}
              onPointerUp={cancelPress}
              onPointerLeave={cancelPress}
              onPointerCancel={cancelPress}
              onContextMenu={(e) => e.preventDefault()}
              data-testid="bn-create"
              aria-label="Quick create — hold to post a prayer"
              title="Tap for quick create · Hold to post a prayer"
              className="w-14 h-14 rounded-full bg-cream text-ink grid place-items-center shadow-2xl ring-4 ring-ink active:scale-95 transition-transform select-none"
            >
              <Plus size={28} weight="bold" />
            </button>
          </li>

          {/* Orange mic — voice commands on every page. */}
          <li className="h-full">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("holywords:open-voice"))}
              data-testid="bn-voice"
              aria-label="Voice commands"
              className="w-full h-full flex flex-col items-center justify-center gap-0.5 pt-1 text-cream/70 active:scale-95 transition-transform"
            >
              <Microphone size={22} weight="fill" className="text-terracotta" />
              <span className="text-[9px] font-semibold leading-none pb-1.5">Voice</span>
            </button>
          </li>

          {/* Today — daily devotional. Shows a tiny date pill (MAY 17)
              instead of a static icon to feel alive. */}
          <TodaySlot active={isActive("/devotional")} />

          <YouSlot user={user} active={isActive("/me") || isActive("/profile")} />
        </ul>
      </nav>

      {showCreate && <CreateSheet onClose={() => setShowCreate(false)} />}
    </>
  );
}

function readLastPlayed() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem("hw:last-played");
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function NavSlot({ to, label, Icon, active, dot = false, testid }) {
  return (
    <li className="flex items-end justify-center h-full">
      <Link
        to={to}
        data-testid={testid}
        data-active={active ? "1" : "0"}
        className="relative flex flex-col items-center justify-center gap-0.5 pt-2 pb-1.5 w-full h-full"
      >
        <span className="relative">
          <Icon size={24} weight={active ? "fill" : "regular"} />
          {dot && (
            <span
              className="absolute -top-0.5 -right-1 w-2.5 h-2.5 rounded-full bg-terracotta border-2 border-ink animate-pulse"
              aria-hidden="true"
            />
          )}
        </span>
        <span className={`text-[10px] tracking-wide ${active ? "font-bold" : "font-medium opacity-90"}`}>
          {label}
        </span>
      </Link>
    </li>
  );
}

function ListenSlot({ lastPlayed, playerOpen, active, onResume }) {
  // Tap behavior:
  //   • If we have a resumable last-played blob → pop the mini-player
  //     back open playing exactly what the user was on. Stay on the
  //     current page so they don't lose their place.
  //   • Otherwise → route them to the Sermons library so they have
  //     something to start with.
  const navigate = useNavigate();
  const canResume = !!(lastPlayed && (lastPlayed.youtube_id || lastPlayed.mp4_url || lastPlayed.archive_id));
  const handleClick = (e) => {
    if (canResume && onResume) {
      e.preventDefault();
      const ok = onResume();
      if (!ok) navigate(lastPlayed?.from_path || "/sermons");
    }
  };
  const fallbackTo = lastPlayed?.from_path || "/sermons";
  return (
    <li className="flex items-end justify-center h-full">
      <Link
        to={fallbackTo}
        onClick={handleClick}
        data-testid="bn-listen"
        data-active={active ? "1" : "0"}
        title={lastPlayed?.title ? `Resume: ${lastPlayed.title}` : "Browse sermons & gospel"}
        className="relative flex flex-col items-center justify-center gap-0.5 pt-2 pb-1.5 w-full h-full"
      >
        <span className="relative">
          <Headphones size={24} weight={active ? "fill" : "regular"} />
          {/* Pulsing dot when there's an active mini-player track or a
              saved last-played within the last 24 h. Gives the slot a
              "you've got something paused" vibe — high-pull magnet. */}
          {(playerOpen || (lastPlayed && Date.now() - (lastPlayed.saved_at || 0) < 24 * 3600 * 1000)) && (
            <span
              className="absolute -top-0.5 -right-1 w-2.5 h-2.5 rounded-full bg-terracotta border-2 border-ink animate-pulse"
              aria-hidden="true"
              data-testid="bn-listen-pulse"
            />
          )}
        </span>
        <span className={`text-[10px] tracking-wide ${active ? "font-bold" : "font-medium opacity-90"}`}>
          Listen
        </span>
      </Link>
    </li>
  );
}

function TodaySlot({ active }) {
  // Tiny date pill so the slot reads as a daily ritual. We deliberately
  // hide the actual date number behind the cream pill on inactive so
  // it never feels stale.
  const monthDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" })
    .format(new Date())
    .toUpperCase();
  return (
    <li className="flex items-end justify-center h-full">
      <Link
        to="/devotional"
        data-testid="bn-today"
        data-active={active ? "1" : "0"}
        title={`Today's devotional · ${monthDay}`}
        className="relative flex flex-col items-center justify-center gap-0.5 pt-2 pb-1.5 w-full h-full"
      >
        <span className="relative">
          <Sun size={24} weight={active ? "fill" : "regular"} />
          {/* Date pill — tiny tracking-wider chip under the icon */}
          <span
            className={`absolute -bottom-1 left-1/2 -translate-x-1/2 px-1 h-3.5 rounded-full text-[8px] font-bold tracking-wider grid place-items-center ${
              active ? "bg-cream text-ink" : "bg-terracotta text-cream"
            }`}
            data-testid="bn-today-pill"
          >
            {monthDay}
          </span>
        </span>
        <span className={`text-[10px] tracking-wide mt-1.5 ${active ? "font-bold" : "font-medium opacity-90"}`}>
          Today
        </span>
      </Link>
    </li>
  );
}

function YouSlot({ user, active }) {
  return (
    <li className="flex items-end justify-center h-full">
      <Link
        to={user ? "/me" : "/login"}
        data-testid="bn-you"
        data-active={active ? "1" : "0"}
        className="relative flex flex-col items-center justify-center gap-0.5 pt-2 pb-1.5 w-full h-full"
      >
        {user ? (
          // Same universal avatar as the "My page" chip: uploaded photo →
          // backend avatar endpoint → colored initials. `picture` (Google
          // photo) kept as a fallback source.
          <span className={`rounded-full ${active ? "ring-2 ring-cream" : "ring-1 ring-cream/40"}`}>
            <UserAvatar
              user={{ id: user.id, name: user.name, avatar_url: user.avatar_url || user.picture }}
              size={26}
              testId="bn-you-avatar"
            />
          </span>
        ) : (
          <UserCircle size={26} weight={active ? "fill" : "regular"} />
        )}
        <span className={`text-[10px] tracking-wide ${active ? "font-bold" : "font-medium opacity-90"}`}>
          You
        </span>
      </Link>
    </li>
  );
}

function CreateSheet({ onClose }) {
  const { user, logout } = useAuth?.() || { user: null, logout: null };
  // ESC key closes — extra safety net for desktop users who might be
  // confused by floating widgets that cover the X close button.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <div
      className="fixed inset-0 z-[120] bg-ink/70 flex items-end md:items-end justify-center md:pb-24"
      onClick={onClose}
      data-testid="create-sheet"
    >
      <div
        className="bg-cream w-full md:w-[28rem] max-w-full rounded-t-3xl md:rounded-3xl p-4 md:p-6"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)" }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl text-forest">What would you like to do?</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="create-sheet-close"
            className="w-9 h-9 grid place-items-center rounded-full hover:bg-cream-200 text-ink"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {ACTIONS.map((a) => {
            const Icon = a.icon;
            return (
              <Link
                key={a.to}
                to={a.to}
                onClick={onClose}
                data-testid={`create-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-cream-200 hover:bg-forest hover:text-cream text-ink transition-all"
              >
                <Icon size={20} weight="fill" className="shrink-0" />
                <span className="text-sm font-semibold">{a.label}</span>
              </Link>
            );
          })}
        </div>
        {/* Hint that explains the long-press shortcut so the gesture
            isn't a secret-handshake. */}
        <p className="mt-3 text-[11px] text-muted_ink text-center">
          Tip: <span className="font-bold">long-press the + button</span> to jump straight to the Prayer Wall.
        </p>
        {/* Bottom-of-thumb-zone Sign In / Sign Out so iPhone users
            (especially big-screen models) don't have to stretch to
            the top-right header to manage auth. Added 2026-06-12. */}
        {user ? (
          <button
            type="button"
            onClick={() => { try { logout?.(); } catch (_) { /* logout best-effort */ } onClose(); }}
            data-testid="create-sheet-signout"
            className="mt-3 w-full h-11 rounded-full bg-terracotta text-cream font-bold text-[11px] tracking-[0.18em] uppercase hover:bg-terracotta/90 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
          >
            <SignOut size={14} weight="bold" /> Sign out
          </button>
        ) : (
          <Link
            to="/login"
            onClick={onClose}
            data-testid="create-sheet-signin"
            className="mt-3 w-full h-11 rounded-full bg-forest text-cream font-bold text-[11px] tracking-[0.18em] uppercase hover:bg-forest/90 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
          >
            <SignIn size={14} weight="bold" /> Sign in
          </Link>
        )}
        {/* Big secondary close — fallback so users who can't see the
            top-right X (sometimes covered by floating widgets on
            desktop) always have an obvious way out. User report
            (Feb 2026): "shortcut tab blocks the icon to reclose it". */}
        <button
          type="button"
          onClick={onClose}
          data-testid="create-sheet-close-bottom"
          className="mt-4 w-full h-11 rounded-full bg-forest text-cream font-bold text-[11px] tracking-[0.18em] uppercase hover:bg-forest/90 active:scale-[0.98] transition-all"
        >
          Close
        </button>
      </div>
    </div>,
    document.body
  );
}
